import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BuddyMailAttachment } from '@adornis/bh-matching-mails/entities/attachment.js';
import { BuddyMailTemplate, convertUserToMailVarObject } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { getMenteeUserClass } from '@adornis/bh-matching-matches/user-class.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { concatMap, filter, firstValueFrom, from, map, Observable, switchMap, timer } from 'rxjs';
import { BuddyUser } from '../entities/buddy-user.js';
import { MatchingPermission } from '../permissions.js';
const getInTimeFrameCount = async (
  userClass: string,
  timeframeID: string,
  onlyUnmatched: boolean,
  filter?: AdornisFilter,
) => {
  const collection = await getRawCollection<BuddyUser>(BuddyUser._collectionName);
  const isMenteeClass = userClass === getMenteeUserClass()._class;
  const numMatchesAspect = filter?.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;

  const result = (
    await collection
      .aggregate<BuddyUser>([
        { $match: { _class: userClass, timeframeID } },
        {
          $lookup: {
            from: Match._collectionName,
            as: 'matches',
            localField: '_id',
            foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
          },
        },
        ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
        ...(numMatchesAspect
          ? [
              { $addFields: { numMatches: { $size: '$matches' } } },
              {
                $match: { numMatches: { $gte: numMatchesAspect.min ?? 0, $lte: numMatchesAspect.max ?? Infinity } },
              },
            ]
          : []),
      ])
      .toArray()
  ).length;

  return result;
};

const subscribeAllBuddyInCurrentTimeframeResolver = (
  userClass: string,
  timeframeID: string,
  filter: AdornisFilter,
  onlyUnmatched: boolean,
  skip?: number,
  limit?: number,
) => {
  const query = { _class: userClass, timeframeID };
  return watchQuery(BuddyUser._collectionName, query).pipe(
    switchMap(async users => {
      const numMatchesAspect = filter.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;
      const isMenteeClass = userClass === getMenteeUserClass()._class;

      const aggregation = [
        { $match: query },
        {
          $lookup: {
            from: Match._collectionName,
            as: 'matches',
            localField: '_id',
            foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
          },
        },
        ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
        { $addFields: { numMatches: { $size: '$matches' } } },
        { $match: filter.getMongoFilter() },
        { $sort: filter.getMongoOptions().sort },
        ...(numMatchesAspect
          ? [
              {
                $match: { numMatches: { $gte: numMatchesAspect.min ?? 0, $lte: numMatchesAspect.max ?? Infinity } },
              },
            ]
          : []),
        ...(isMenteeClass
          ? [
              {
                $lookup: {
                  from: BuddyUser._collectionName,
                  as: 'preferedMentors',
                  localField: 'preferedMentorsIDs',
                  foreignField: '_id',
                },
              },
            ]
          : []),
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ];

      const result = await (
        await getRawCollection(BuddyUser._collectionName)
      )
        .aggregate<EntityData<BuddyUser>>(aggregation, {
          collation: { locale: 'en', strength: 1 },
        })
        .toArray();
      return result as unknown as BuddyUser[];
    }),
  ) as Observable<BuddyUser[]>;
};

export const getAllBuddiesInCurrentTimeframeResolver = async (
  userClass: string,
  timeframeID: string,
  filter: AdornisFilter,
  onlyUnmatched: boolean,
  skip?: number,
  limit?: number,
) => {
  const collection = await getRawCollection<BuddyUser>(BuddyUser._collectionName);
  const isMenteeClass = userClass === getMenteeUserClass()._class;
  const query = { _class: userClass, timeframeID };
  const numMatchesAspect = filter.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;
  return await collection
    .aggregate<BuddyUser>([
      { $match: query },
      {
        $lookup: {
          from: Match._collectionName,
          as: 'matches',
          localField: '_id',
          foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
        },
      },
      ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
      { $addFields: { numMatches: { $size: '$matches' } } },
      { $match: filter.getMongoFilter() },
      ...(numMatchesAspect
        ? [{ $match: { numMatches: { $gte: numMatchesAspect.min ?? 0, $lte: numMatchesAspect.max ?? Infinity } } }]
        : []),
      ...(skip ? [{ $skip: skip }] : []),
      ...(limit ? [{ $limit: limit }] : []),
      ...(isMenteeClass
        ? [
            {
              $lookup: {
                from: BuddyUser._collectionName,
                as: 'preferedMentors',
                localField: 'preferedMentorsIDs',
                foreignField: '_id',
              },
            },
          ]
        : []),
    ])
    .toArray();
};

const subscribeAllUnmatchedBuddiesInCurrentTimeframe = registerSubscription({
  operationName: 'subscribeAllUnmatchedBuddiesInCurrentTimeframe',
  resolve:
    (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) =>
      subscribeAllBuddyInCurrentTimeframeResolver(userClass, timeframeID, filter, true, skip, limit),
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

const subscribeAllBuddiesInCurrentTimeframe = registerSubscription({
  operationName: 'subscribeAllBuddiesInCurrentTimeframe',
  resolve:
    (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) =>
      subscribeAllBuddyInCurrentTimeframeResolver(userClass, timeframeID, filter, false, skip, limit),
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

const saveNoteOnBuddyUser = registerMutation({
  operationName: 'saveNoteOnBuddyUser',
  resolve: (userID: string, note: string) => async () => {
    const currUser = await CurrentUserInfo.getMyself<BuddyUser>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!context.serverContext && !currUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
      throw new Error('No permission to edit notes on users');

    const updateResult = await (
      await getRawCollection(BuddyUser._collectionName)
    ).updateOne({ _id: userID }, { $set: { note } });
    if (updateResult.modifiedCount === 1) return true;
    throw new Error(`Couldnt update note on user (userID: ${userID}; ${JSON.stringify(updateResult)})`);
  },
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'note', type: () => String },
  ],
});

async function getUsersPairsByMatches(matchIDs: string[], userClass?: string) {
  const matches = await (await getCollection<Match>(Match._class)).find({ _id: { $in: matchIDs } }).toArray();
  const userCollection = await getCollection<BuddyUser>(BuddyUser._class);
  const pairs = await Promise.all(
    matches.map(async match => {
      return {
        mentee: await userCollection.findOne({ _id: match.menteeID }),
        mentor: await userCollection.findOne({ _id: match.mentorID }),
        userClass,
      };
    }),
  );
  return pairs;
}

type Pairs = {
  mentee: EntityData<BuddyUser>;
  mentor: EntityData<BuddyUser>;
  userClass: string | undefined;
};

const sendFreeFormMailToBuddyUsers = registerSubscription({
  operationName: 'sendFreeFormMailToBuddyUsers',
  resolve: (matchIDs: string[], mailTemplate: BuddyMailTemplate, userClass?: string) => () => {
    return from(
      new Promise<[Pairs[], BuddyMailAttachment[]]>(async resolve => {
        const pairs = (await getUsersPairsByMatches(matchIDs, userClass)) as Pairs[];

        const attachments = (await getByID(
          BuddyMailAttachment,
          mailTemplate.activatedAttachments,
        )(selectionSet(() => BuddyMailAttachment, 2))) as BuddyMailAttachment[];

        return resolve([pairs, attachments]);
      }),
    ).pipe(
      switchMap(([pairs, attachments]) =>
        from(pairs).pipe(
          concatMap(async (pair, i) => {
            // dont bulk send all emails, especially for larger amounts
            await firstValueFrom(timer(100));
            const mailVars = {
              ...(pair.mentee ? convertUserToMailVarObject(constructValue<BuddyUser>(pair.mentee), 'mentee') : {}),
              ...(pair.mentor ? convertUserToMailVarObject(constructValue<BuddyUser>(pair.mentor), 'mentor') : {}),
            };
            const targetMailTargets = pair.userClass
              ? pair.userClass.includes('Mentee')
                ? [pair.mentee?.email]
                : [pair.mentor?.email]
              : [pair.mentee?.email, pair.mentor?.email];

            // send mail to all recipients standalone
            for await (const targetMail of targetMailTargets)
              await (
                await mailer
              ).sendMail(
                await Message.compose({
                  html: (await mailTemplate.buildEmail(mailVars)) ?? '',
                  subject: (await mailTemplate.buildSubject(mailVars)) ?? '',
                  attachmentPaths: attachments.map(
                    attachment =>
                      `${attachment.getServeLink()}/${attachment.meta?.fileName ?? 'file'}.${
                        attachment.meta?.extension ?? 'txt'
                      }`,
                  ),
                }),
                new Recipient([targetMail], undefined, undefined),
              );

            return i + 1;
          }),
        ),
      ),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
    { name: 'timeframeID', type: () => ID },
    { name: 'userClass', type: () => String },
  ],
});

const sendFreeFormMailToBuddyUser = registerMutation({
  operationName: 'sendFreeFormMailToBuddyUser',
  resolve: (userID: string, mailTemplate: BuddyMailTemplate) => async () => {
    const userCol = await getRawCollection(User._collectionName);
    const user = await userCol.findOne({ _id: userID });
    if (!user) throw new Error('User not found: ' + userID);

    const userType = user._class === getMenteeUserClass()._class ? 'mentee' : 'mentor';
    const mailVars = convertUserToMailVarObject(constructValue<BuddyUser>(user), userType);
    await (
      await mailer
    ).sendMail(
      await Message.compose({
        html: (await mailTemplate.buildEmail(mailVars)) ?? '',
        subject: (await mailTemplate.buildSubject(mailVars)) ?? '',
      }),
      new Recipient([user.email], undefined, undefined),
    );
  },
  type: () => String,
  params: [
    { name: 'userID', type: () => String },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
  ],
});

const sendFreeFormMailToBuddyMatch = registerSubscription({
  operationName: 'sendFreeFormMailToBuddyMatch',
  resolve: (matchingID: string, mailTemplate: BuddyMailTemplate, userClass?: string) => () => {
    return from(
      new Promise<[[EntityData<BuddyUser>, EntityData<BuddyUser>], BuddyMailAttachment[], string | undefined]>(
        async resolve => {
          const match = await (await getCollection<Match>(Match._class)).findOne({ _id: matchingID });
          if (!match) throw new Error('Match not found');
          const userCollection = await getCollection<BuddyUser>(BuddyUser._class);

          const mentor = await userCollection.findOne<BuddyUser>({ _id: match.mentorID });
          if (!mentor) throw new Error('Mentor not found');

          const mentee = await userCollection.findOne<BuddyUser>({ _id: match.menteeID });
          if (!mentee) throw new Error('Mentee not found');

          const attachments = await getByID(
            BuddyMailAttachment,
            mailTemplate.activatedAttachments,
          )(selectionSet(() => BuddyMailAttachment, 2));

          return resolve([[mentor, mentee], attachments, userClass]);
        },
      ),
    ).pipe(
      switchMap(([users, attachments, userClass]) =>
        from(users).pipe(
          map(user => <const>[user, attachments, users]),
          filter(([user, _, __]) => (userClass ? user._class === userClass : false)),
          concatMap(async ([user, attachments, users]) => {
            // dont bulk send all emails, especially for larger amounts
            await firstValueFrom(timer(100));

            await (
              await mailer
            ).sendMail(
              await Message.compose({
                html: (await mailTemplate.buildEmail(user)) ?? '',
                subject: (await mailTemplate.buildSubject(user)) ?? '',
                attachmentPaths: attachments.map(
                  attachment =>
                    `${attachment.getServeLink()}/${attachment.meta?.fileName ?? 'file'}.${
                      attachment.meta?.extension ?? 'txt'
                    }`,
                ),
              }),
              new Recipient(user.email, undefined, undefined),
            );
            return (users.indexOf(user) + 1).toString();
          }),
        ),
      ),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => BuddyMailTemplate },
    { name: 'userClass', type: () => String },
  ],
});

export const getAllUnsmatchedBuddiesInCurrentTimeframe = registerQuery({
  operationName: 'getAllUnsmatchedBuddiesInCurrentTimeframe',
  type: () => [BuddyUser],
  resolve: (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) => {
    return (gqlFields: BaseQLSelectionSet<BuddyUser>) => {
      return getAllBuddiesInCurrentTimeframeResolver(userClass, timeframeID, filter, true, skip, limit);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const getAllBuddiesInCurrentTimeframe = registerQuery({
  operationName: 'getAllBuddiesInCurrentTimeframe',
  type: () => [BuddyUser],
  resolve: (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) => {
    return (gqlFields: BaseQLSelectionSet<BuddyUser>) => {
      return getAllBuddiesInCurrentTimeframeResolver(userClass, timeframeID, filter, false, skip, limit);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const _getInCurrentTimeFrameCount = registerQuery({
  operationName: 'getInCurrentTimeframeCount',
  type: () => Int,
  resolve: (userClass: string, timeframeID: string, filter?: AdornisFilter) => {
    return async () => {
      return await getInTimeFrameCount(userClass, timeframeID, false, filter);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

export const _getUnmatchedInCurrentTimeFrameCount = registerQuery({
  operationName: 'getUnmatchedInCurrentTimeframeCount',
  type: () => Int,
  resolve: (userClass: string, timeframeID: string) => {
    return async () => {
      return await getInTimeFrameCount(userClass, timeframeID, true);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
  ],
});

export namespace MatchingUserAPI {
  export const sendFreeFormMailToUser = sendFreeFormMailToBuddyUsers;
  export const sendFreeFormMailToSingleUser = sendFreeFormMailToBuddyUser;
  export const sendFreeFormMailToMatch = sendFreeFormMailToBuddyMatch;
  export const saveNote = saveNoteOnBuddyUser;
  export const subscribeAllInCurrentTimeframe = subscribeAllBuddiesInCurrentTimeframe;
  export const subscribeAllUnmatchedInCurrentTimeframe = subscribeAllUnmatchedBuddiesInCurrentTimeframe;
  export const getAllUnsmatchedInCurrentTimeframe = getAllUnsmatchedBuddiesInCurrentTimeframe;
  export const getAllInCurrentTimeframe = getAllBuddiesInCurrentTimeframe;
  export const getInCurrentTimeframeCount = _getInCurrentTimeFrameCount;
  export const getUnmatchedInCurrentTimeframeCount = _getUnmatchedInCurrentTimeFrameCount;
}
